/* eslint-disable curly */
import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import { t } from "@excalidraw/excalidraw/i18n";
import { useEffect, useRef, useState } from "react";

import "./LibraryDialogs.scss";

export const PromptDialog = ({
  title,
  label,
  initialValue,
  submitLabel,
  onSubmit,
  onClose,
}: {
  title: string;
  label?: string;
  initialValue: string;
  submitLabel?: string;
  onSubmit: (value: string) => void;
  onClose: () => void;
}) => {
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setValue(initialValue);
    const t = setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
    return () => clearTimeout(t);
  }, [initialValue]);

  const submit = () => {
    const v = value.trim();
    if (!v) return;
    onSubmit(v);
  };

  return (
    <Dialog size="small" onCloseRequest={onClose} title={title}>
      <form
        className="library-dialog"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {label && <label className="library-dialog__label">{label}</label>}
        <input
          ref={inputRef}
          className="library-dialog__input"
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <div className="library-dialog__actions">
          <button
            type="button"
            className="library-dialog__btn"
            onClick={onClose}
          >
            {t("canvasLibrary.cancel")}
          </button>
          <button
            type="submit"
            className="library-dialog__btn library-dialog__btn--primary"
            disabled={!value.trim()}
          >
            {submitLabel ?? t("canvasLibrary.ok")}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

type NewCanvasType = "canvas" | "notebook";
type NewPageStyle = "blank" | "ruled" | "grid" | "dotted";

export const NewCanvasDialog = ({
  initialValue,
  onSubmit,
  onClose,
}: {
  initialValue: string;
  onSubmit: (value: {
    name: string;
    canvas_type: NewCanvasType;
    page_style: NewPageStyle;
  }) => void;
  onClose: () => void;
}) => {
  const [value, setValue] = useState(initialValue);
  const [type, setType] = useState<NewCanvasType>("canvas");
  const [pageStyle, setPageStyle] = useState<NewPageStyle>("ruled");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setValue(initialValue);
    const tid = setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
    return () => clearTimeout(tid);
  }, [initialValue]);

  const submit = () => {
    const v = value.trim();
    if (!v) return;
    onSubmit({ name: v, canvas_type: type, page_style: pageStyle });
  };

  const styleOptions: { key: NewPageStyle; label: string }[] = [
    { key: "ruled", label: t("notebook.style.ruled") },
    { key: "grid", label: t("notebook.style.grid") },
    { key: "dotted", label: t("notebook.style.dotted") },
    { key: "blank", label: t("notebook.style.blank") },
  ];

  return (
    <Dialog
      size="small"
      onCloseRequest={onClose}
      title={t("canvasLibrary.newCanvas")}
    >
      <form
        className="library-dialog"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <label className="library-dialog__label">
          {t("canvasLibrary.name")}
        </label>
        <input
          ref={inputRef}
          className="library-dialog__input"
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />

        <div className="library-dialog__field">
          <span className="library-dialog__label">
            {t("notebook.typeLabel")}
          </span>
          <div className="library-dialog__seg">
            <button
              type="button"
              className={`library-dialog__seg-btn ${
                type === "canvas" ? "library-dialog__seg-btn--active" : ""
              }`}
              onClick={() => setType("canvas")}
            >
              {t("notebook.typeCanvas")}
            </button>
            <button
              type="button"
              className={`library-dialog__seg-btn ${
                type === "notebook" ? "library-dialog__seg-btn--active" : ""
              }`}
              onClick={() => setType("notebook")}
            >
              {t("notebook.typeNotebook")}
            </button>
          </div>
        </div>

        {type === "notebook" && (
          <div className="library-dialog__field">
            <span className="library-dialog__label">
              {t("notebook.pageStyleLabel")}
            </span>
            <div className="library-dialog__styles">
              {styleOptions.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  className={`library-dialog__style ${
                    pageStyle === opt.key ? "library-dialog__style--active" : ""
                  }`}
                  onClick={() => setPageStyle(opt.key)}
                >
                  <span
                    className={`library-dialog__style-swatch library-dialog__style-swatch--${opt.key}`}
                  />
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="library-dialog__actions">
          <button
            type="button"
            className="library-dialog__btn"
            onClick={onClose}
          >
            {t("canvasLibrary.cancel")}
          </button>
          <button
            type="submit"
            className="library-dialog__btn library-dialog__btn--primary"
            disabled={!value.trim()}
          >
            {t("canvasLibrary.create")}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

export const ConfirmDialog = ({
  title,
  message,
  confirmLabel,
  danger = false,
  onConfirm,
  onClose,
}: {
  title: string;
  message: string;
  confirmLabel?: string;
  danger?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) => {
  return (
    <Dialog size="small" onCloseRequest={onClose} title={title}>
      <div className="library-dialog">
        <p className="library-dialog__message">{message}</p>
        <div className="library-dialog__actions">
          <button
            type="button"
            className="library-dialog__btn"
            onClick={onClose}
          >
            {t("canvasLibrary.cancel")}
          </button>
          <button
            type="button"
            className={`library-dialog__btn ${
              danger
                ? "library-dialog__btn--danger"
                : "library-dialog__btn--primary"
            }`}
            onClick={() => {
              onConfirm();
              onClose();
            }}
            autoFocus
          >
            {confirmLabel ?? t("canvasLibrary.confirm")}
          </button>
        </div>
      </div>
    </Dialog>
  );
};
